import {useEffect, useMemo, useState} from 'react';
import {QueryClient, QueryClientProvider, useMutation, useQuery, useQueryClient,} from '@tanstack/react-query';
import CatalogTable from "./CatalogTable";
import useHardwareCatalog from "../Hooks/UseHardwareCatalog";
import {Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid} from "@mui/material";
import {validateEntity} from "../../../General/SharedMethods";
import {ACTUATOR, ALGO_PARAMETERS, COMMUNICATION_INTERFACE, COMPATIBLE_CONTROL_UNIT_TYPES, ELECTRICAL_INTERFACE_TYPE, MANUFACTURER, REGION, RETURN_METER, RETURN_VALVE, SUPPLY_METER, SUPPLY_VALVE, VALVE} from "../Constants/constants";

const CatalogEntity = ({
                           entityType,
                           entityName,
                           createColumns,
                           handleCustomSaveEntity,
                           renderCustomAction,
                           customEditDialog,
                           customCreateDialog,
                           customViewDialog,
                           enums,
                           validationSchema
                       }) => {
    const [validationErrors, setValidationErrors] = useState({});
    const {getAll, create, update, remove} = useHardwareCatalog();
    const [columnFilters, setColumnFilters] = useState([]);
    const [globalFilter, setGlobalFilter] = useState('');
    const [sorting, setSorting] = useState([]);
    const [pagination, setPagination] = useState({pageIndex: 0, pageSize: 13});
    const [totalElements, setTotalElements] = useState(0);
    const [errorMessage, setErrorMessage] = useState();
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [entityIdToDelete, setEntityIdToDelete] = useState(null);
    const columns = useMemo(
        () => {
            return createColumns(validationErrors, setValidationErrors);
        }, [validationErrors]);

    const {mutateAsync: createEntity, isPending: isCreatingEntity} =
        useCreateEntity(entityType, create, setErrorMessage);

    const {
        data: fetchedEntities = [],
        isError: isLoadingEntitiesError,
        isFetching: isFetchingEntities,
        isLoading: isLoadingEntities,
        refetch: refetch,
    } = useGetEntities(entityType, getAll, globalFilter, sorting, pagination, columnFilters, setTotalElements, columns, enums);

    const {mutateAsync: updateEntity, isPending: isUpdatingEntity} =
        useUpdateEntity(entityType, update, setErrorMessage);

    const {mutateAsync: deleteEntity, isPending: isDeletingEntity} =
        useDeleteEntity(entityType, remove, setErrorMessage);

    const handleCreateEntity = async ({values, table}) => {
        const newValidationErrors = await validateEntity(values, validationSchema);
        if (Object.values(newValidationErrors).some((error) => error)) {
            setValidationErrors(newValidationErrors);
            return;
        }
        setValidationErrors({});
        createEntity(values);
        table.setCreatingRow(null);
    };

    const handleSaveEntity = async ({values, table}) => {
        const newValidationErrors = await validateEntity(values, validationSchema);
        if (Object.values(newValidationErrors).some((error) => error)) {
            setValidationErrors(newValidationErrors);
            return;
        }
        setValidationErrors({});
        updateEntity(values);
        table.setEditingRow(null);
    };


    const openDeleteConfirmModal = (row) => {
        setOpenDeleteDialog(true);
        setEntityIdToDelete(row.original.id);

    };

    const clearErrorMessage = () => {
        setErrorMessage("");
    };

    useEffect(() => {
        refetch();
    }, [globalFilter, sorting, pagination, columnFilters, refetch]);

    return <>
        {errorMessage && (
            <Alert severity="error" onClose={clearErrorMessage}>
                {errorMessage}
            </Alert>
        )}
        <Dialog open={openDeleteDialog}>
            <DialogTitle variant="h4">{`Delete ${entityName}`}</DialogTitle>
            <DialogContent>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        Are you sure you want to delete this entity?
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => {
                    setEntityIdToDelete(null);
                    setOpenDeleteDialog(false)
                }}>No</Button>
                <Button onClick={() => {
                    deleteEntity(entityIdToDelete)
                    setOpenDeleteDialog(false)
                }}>Yes</Button>
            </DialogActions>
        </Dialog>
        <CatalogTable columns={columns}
                      totalElements={totalElements}
                      isLoadingEntitiesError={isLoadingEntitiesError}
                      fetchedEntities={fetchedEntities}
                      isFetchingEntities={isFetchingEntities}
                      isLoadingEntities={isLoadingEntities}
                      isCreatingEntity={isCreatingEntity}
                      isUpdatingEntity={isUpdatingEntity}
                      isDeletingEntity={isDeletingEntity}
                      handleCreateEntity={handleCreateEntity}
                      handleSaveEntity={handleSaveEntity}
                      handleCustomSaveEntity={handleCustomSaveEntity}
                      renderCustomAction={renderCustomAction}
                      openDeleteConfirmModal={openDeleteConfirmModal}
                      validationErrors={validationErrors}
                      setValidationErrors={setValidationErrors}
                      entityName={entityName}
                      setColumnFilters={setColumnFilters}
                      setGlobalFilter={setGlobalFilter}
                      setPagination={setPagination}
                      setSorting={setSorting}
                      columnFilters={columnFilters}
                      globalFilter={globalFilter}
                      pagination={pagination}
                      sorting={sorting}
                      customEditDialog={customEditDialog}
                      customCreateDialog={customCreateDialog}
                      customViewDialog={customViewDialog}
        />
    </>;
};

const useCreateEntity = (entityType, create, setErrorMessage) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (entity) => {
            await create(entityType, entity);
        },
        onMutate: async (newEntityInfo) => {
            await queryClient.cancelQueries([entityType]);
            const previousEntities = queryClient.getQueryData([entityType]);
            queryClient.setQueryData([entityType], (prevEntities) => [
                ...prevEntities,
                {...newEntityInfo},
            ]);
            return {previousEntities, newEntityInfo};
        },
        onError: (err, newEntityInfo, context) => {
            setErrorMessage(err.message || "An error occurred");
            queryClient.setQueryData([entityType], context.previousEntities);
        },
        onSettled: () => {
            queryClient.invalidateQueries([entityType]);
        },
    });
}

const handleMultiSelectFilter = (filterId, filter, columnFiltersQuery, enums) => {
    let items;
    switch (filterId) {
        case MANUFACTURER:
        case ACTUATOR:
        case VALVE:
        case REGION:
        case ALGO_PARAMETERS:
        case SUPPLY_METER:
        case SUPPLY_VALVE:
        case RETURN_METER:
        case RETURN_VALVE:
            items = filter.value.map(it => `\'${it}\'`).join(',');
            columnFiltersQuery += `${filterId}.name=in=(${items})`;
            break;
        default:
            let enumProp;
            if (filterId === COMMUNICATION_INTERFACE) {
                enumProp = 'communicationInterfaceType';
                filterId = `${COMMUNICATION_INTERFACE}.type`;
            } else if (filterId === ELECTRICAL_INTERFACE_TYPE) {
                enumProp = ELECTRICAL_INTERFACE_TYPE;
                filterId = `${COMMUNICATION_INTERFACE}.${ELECTRICAL_INTERFACE_TYPE}`;
            } else if (filterId === COMPATIBLE_CONTROL_UNIT_TYPES) {
                enumProp = 'controlUnitType';
                filterId = COMPATIBLE_CONTROL_UNIT_TYPES;
            } else {
                enumProp = filterId;
            }
            if (enums.hasOwnProperty(enumProp)) {
                items = filter.value.map(it => {
                    const fieldValue = enums[enumProp][it];
                    return fieldValue ? `\'${fieldValue}\'` : null;
                }).filter(it => it != null).join(',');
            } else {
                items = filter.value.map(it => `\'${it}\'`).join(',');
            }
            columnFiltersQuery += `${filterId}=in=(${items})`;

    }

    return columnFiltersQuery
}


const useGetEntities = (entityType, getAll, globalFilter, sorting, pagination, columnFilters, setTotalElements, columns, enums) => {
    return useQuery({
        queryKey: [entityType],
        queryFn: async () => {
            try {
                const enhancedFilters = columnFilters.map(filter => {
                    const column = columns.find(col => col.accessorKey === filter.id);
                    const filterType = column ? column.muiEditTextFieldProps.type : 'text';
                    return {
                        ...filter,
                        type: filterType,
                    };
                });
                let columnFiltersQuery = enhancedFilters.length > 0 ? '' : null;
                enhancedFilters.forEach((filter, index) => {
                    let filterId;
                    if (filter.id.includes('.')) {
                        let arr = filter.id.split('.');
                        filterId = arr[1] === ELECTRICAL_INTERFACE_TYPE ? arr[1] : arr[0];
                    } else {
                        filterId = filter.id;
                    }
                    columnFiltersQuery += index !== 0 ? ';' : ''
                    switch (filter.type) {
                        case 'select':
                            columnFiltersQuery += `${filterId}==\'${filter.value}\'`;
                            break;
                        case 'number':
                            columnFiltersQuery += `${filterId}==${filter.value}`;
                            break;
                        case 'date':
                            columnFiltersQuery += `${filterId}==\'${filter.value}\'`;
                            break;
                        case 'multi-select':
                            columnFiltersQuery += handleMultiSelectFilter(filterId, filter, columnFiltersQuery, enums);
                            break;
                        default:
                            columnFiltersQuery += `${filterId}=ilike=\'${filter.value}\'`;
                            break;
                    }
                });
                const sortQuery = sorting.length > 0 ? `${sorting[0].id},${sorting[0].desc ? 'desc' : 'asc'}` : null;
                const pageNumber = pagination.pageIndex;
                const pageSize = pagination.pageSize;
                const response = await getAll(entityType, columnFiltersQuery, globalFilter, sortQuery, pageNumber, pageSize);
                if (!response) {
                    console.error("No response from server");
                } else {
                    setTotalElements(response.data.totalElements);
                }
                return response.data.content;
            } catch (error) {
                console.error(error);
            }
        },
        refetchOnWindowFocus: false,
    });
}

const useUpdateEntity = (entityType, update, setErrorMessage) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (entity) => {
            await update(entityType, entity);
        },
        onMutate: async (newEntityInfo) => {
            await queryClient.cancelQueries([entityType]);
            const previousEntities = queryClient.getQueryData([entityType]);
            queryClient.setQueryData([entityType], (prevEntities) =>
                prevEntities?.map((prevEntity) =>
                    prevEntity.id === newEntityInfo.id ? newEntityInfo : prevEntity,
                ),
            );
            return {previousEntities};
        },
        onError: (err, newEntityInfo, context) => {
            setErrorMessage(err.message || "An error occurred");
            queryClient.setQueryData([entityType], context.previousEntities);
        },
        onSettled: () => {
            queryClient.invalidateQueries([entityType]);
        },
    });
}

const useDeleteEntity = (entityType, remove, setErrorMessage) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (entityId) => {
            await remove(entityType, entityId);
        },
        onMutate: async (entityId) => {
            await queryClient.cancelQueries([entityType]);
            const previousEntities = queryClient.getQueryData([entityType]);
            queryClient.setQueryData([entityType], (prevEntities) =>
                prevEntities?.filter((entity) => entity.id !== entityId),
            );

            return {previousEntities};
        },
        onError: (err, entityId, context) => {
            setErrorMessage(err.message || "An error occurred");
            queryClient.setQueryData([entityType], context.previousEntities);
        },
        onSettled: () => {
            queryClient.invalidateQueries([entityType]);
        },
    });
}

const queryClient = new QueryClient();

export const CatalogEntityProviders = ({
                                           entityType,
                                           entityName,
                                           createColumns,
                                           validateEntity,
                                           handleCustomSaveEntity,
                                           renderCustomAction,
                                           customEditDialog,
                                           customCreateDialog,
                                           customViewDialog,
                                           enums,
                                           validationSchema
                                       }) => (
    <QueryClientProvider client={queryClient}>
        <CatalogEntity entityType={entityType}
                       entityName={entityName}
                       createColumns={createColumns}
                       validateEntity={validateEntity}
                       handleCustomSaveEntity={handleCustomSaveEntity}
                       renderCustomAction={renderCustomAction}
                       customEditDialog={customEditDialog}
                       customCreateDialog={customCreateDialog}
                       customViewDialog={customViewDialog}
                       enums={enums}
                       validationSchema={validationSchema}
        />
    </QueryClientProvider>
);

export default CatalogEntityProviders;


